/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-refresh/only-export-components */
import { Row, RowData } from "@tanstack/react-table";
import {
  functionalUpdate,
  makeStateUpdater,
  OnChangeFn,
  Table,
  TableFeature,
  Updater,
  Column,
} from "@tanstack/table-core";

// define types for our new feature's custom state
export const PreviewStates = [0, 1, 2] as const;
export type PreviewState = (typeof PreviewStates)[number] | undefined;

export interface PreviewTableState {
  preview?: PreviewState;
}

// define types for our new feature's table options
export interface PreviewOptions {
  enablePreview?: boolean;
  skipPreview?: boolean;
  onPreviewChange?: OnChangeFn<PreviewState>;
  // Closing the panel because the previewed row is gone, rather than because the user
  // collapsed it - see `closePreview`.
  onPreviewClose?: () => void;
}

// Define types for our new feature's table APIs
export interface PreviewInstance {
  setPreview: (updater: Updater<PreviewState>) => void;
  // Close the panel on a previewed row that no longer exists (deleted, or filtered out of
  // the list). Providers that address the previewed row in the URL path use this to leave
  // that route as well, instead of leaving a dead id behind; where no such handler is
  // wired, it is plain `setPreview(0)`.
  closePreview: () => void;
  isPreviewEnabled(): boolean;
  isSkipPreview(): boolean;
}
export interface PreviewRow {
  isPreviewEnabled(): boolean;
  isSkipPreview(): boolean;
}

export interface PreviewColumn {
  isPreviewEnabled(): boolean;
  isSkipPreview(): boolean;
}

export interface PreviewColumnDef {
  disablePreview?: boolean;
}

// Use declaration merging to add our new feature APIs and state types to TanStack Table's existing types.
declare module "@tanstack/react-table" {
  //merge our new feature's state with the existing table state
  interface TableState extends PreviewTableState {}
  //merge our new feature's options with the existing table options
  interface TableOptionsResolved<TData extends RowData>
    extends PreviewOptions {}
  //merge our new feature's instance APIs with the existing table instance APIs
  interface Table<TData extends RowData> extends PreviewInstance {}
  // if you need to add cell instance APIs...
  // interface Cell<TData extends RowData, TValue> extends PreviewCell
  // if you need to add row instance APIs...
  interface Row<TData extends RowData> extends PreviewRow {}
  // if you need to add column instance APIs...
  interface Column<TData extends RowData, TValue> extends PreviewColumn {}

  // if you need to add header instance APIs...
  // interface Header<TData extends RowData, TValue> extends PreviewHeader

  // Note: declaration merging on `ColumnDef` is not possible because it is a type, not an interface.
  // But you can still use declaration merging on `ColumnDef.meta`
  interface ColumnMeta<TData extends RowData, TValue>
    extends PreviewColumnDef {}
}

// end of TS setup!

// Here is all of the actual javascript code for our new feature
export const PreviewFeature: TableFeature<any> = {
  // define the new feature's initial state
  getInitialState: (state): PreviewTableState => {
    return {
      preview: undefined,
      ...state,
    };
  },

  // define the new feature's default options
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): PreviewOptions => {
    return {
      enablePreview: true,
      skipPreview: false,
      onPreviewChange: makeStateUpdater("preview", table),
    } as PreviewOptions;
  },
  // if you need to add a default column definition...
  // getDefaultColumnDef: <TData extends RowData>(): Partial<ColumnDef<TData>> => {
  //   return { meta: {} } //use meta instead of directly adding to the columnDef to avoid typescript stuff that's hard to workaround
  // },

  // define the new feature's table instance methods
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.setPreview = (updater) => {
      return table.options.onPreviewChange?.((old) => {
        const next = functionalUpdate(updater, old);
        return next;
      });
    };
    table.closePreview = () => {
      const onPreviewClose = table.options.onPreviewClose;
      if (onPreviewClose) {
        onPreviewClose();
        return;
      }
      table.setPreview(0);
    };
    table.isPreviewEnabled = () => table.options.enablePreview ?? true;
    table.isSkipPreview = () => table.options.skipPreview ?? false;
  },

  // if you need to add row instance APIs...
  createRow: <TData extends RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ): void => {
    row.isPreviewEnabled = () => table.isPreviewEnabled();
    row.isSkipPreview = () => table.isSkipPreview();
    //  && !!table.getVisibleFlatColumns().find((c) => c.isPreviewEnabled());
  },
  // if you need to add cell instance APIs...
  // createCell: <TData extends RowData>(cell, column, row, table): void => {},
  // if you need to add column instance APIs...
  createColumn: <TData extends RowData>(
    column: Column<TData>,
    table: Table<TData>,
  ): void => {
    column.isPreviewEnabled = () =>
      table.isPreviewEnabled() && !column.columnDef.meta?.disablePreview;
    column.isSkipPreview = () => table.isSkipPreview();
  },
  // if you need to add header instance APIs...
  // createHeader: <TData extends RowData>(header, table): void => {},
};
//end of custom feature code
